import React, { ReactElement, ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { ColDef } from "@ag-grid-community/core";
import { AgGridReactProps } from "@ag-grid-community/react/lib/agGridReact";
import Tabs from "antd/es/tabs";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import { Modal } from "@mapmycustomers/ui";

import { skipNonListViewFields } from "@app/component/SelectColumnsPane/util/fieldFilter";
import AgGridListViewProvider from "@app/component/view/ListView/AgGridListViewProvider";
import AgGridListViewSection from "@app/component/view/ListView/AgGridListViewSection";
import AgGridListViewPagination from "@app/component/view/ListView/components/Pagination/AgGridListViewPagination";
import ExportButton from "@app/scene/dashboard/components/cards/common/DrillDownModal/components/ExportButton";
import SelectColumnsButton from "@app/scene/dashboard/components/cards/common/DrillDownModal/components/SelectColumnsButton";
import { DrillDownListFetcherPayload } from "@app/scene/dashboard/types/DrillDownListFetcher";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { TabbedDrillDownListFetcherPayload } from "@app/scene/dashboard/types/TabbedDrillDownListFetcher";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType, {
  CanonicalEntityTypeWithFieldModel,
} from "@app/util/fieldModel/getByEntityType";
import { getEntityTypeDisplayName } from "@app/util/ui";
import useHiddenChat from "@app/util/useHiddenChat";

import Actions from "./components/Actions";
import Grid from "./components/Grid";
import styles from "./DrillDownModal.module.scss";

const EMPTY_LIST_VIEW_STATE = {
  columns: [],
  filter: {},
  range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
  sort: [],
  viewAs: undefined,
} as Readonly<ViewState>;

export interface TabbedDrillDownModalProps<
  ET extends CanonicalEntityTypeWithFieldModel,
  VS extends DrillDownViewState = DrillDownViewState
> {
  allEntityTypes: ET[];
  context?: AgGridReactProps["context"];
  entityTypes: ET[];
  gridFrameworkComponents?: AgGridReactProps["frameworkComponents"];
  loading?: boolean;
  onActivityTypeView?: (entity: AnyEntity, element: HTMLElement) => void;
  onEdit?: () => void;
  onExportDrillDownData?: (entityType: ET) => void;
  onFetchDrillDownData: (payload: TabbedDrillDownListFetcherPayload<AnyEntity, ET>) => void;
  onHide: () => void;
  onRecordView?: (entity: AnyEntity) => void;
  subtitle?: ReactNode;
  title: ReactNode;
  totalFilteredRecords: Partial<Record<ET, number>>;
  viewStates: Partial<Record<ET, VS>>;
}

const TabbedDrillDownModal = <
  ET extends CanonicalEntityTypeWithFieldModel,
  VS extends DrillDownViewState = DrillDownViewState
>({
  allEntityTypes,
  canDownload,
  context,
  entityTypes,
  gridFrameworkComponents,
  loading,
  onActivityTypeView,
  onEdit,
  onExportDrillDownData,
  onFetchDrillDownData,
  onHide,
  onRecordView,
  subtitle,
  title,
  totalFilteredRecords,
  viewStates,
}: TabbedDrillDownModalProps<ET, VS> & ReturnType<typeof mapStateToProps>) => {
  const analytics = useExtendedAnalytics("DrillDown", useAnalytics());

  useHiddenChat();
  const intl = useIntl();

  const [entityType, setEntityType] = useState<ET>(entityTypes[0]);
  const handleChangeTab = useCallback(
    (key: string) => {
      analytics.clicked(["Tab"], { key });
      setEntityType(key as ET);
    },
    [analytics]
  );

  const columnDefs: Record<ET, ColDef[]> = useMemo(() => {
    return (Object.keys(viewStates) as ET[]).reduce((result, entityType) => {
      result[entityType] = (viewStates[entityType]?.columns ?? [])
        .filter(({ field }) => field.isReadable || field.isSystemRequired)
        .map<ColDef>(({ field, pinned, visible, width }) => ({
          ...(field.gridProperties(false) ?? {}),
          hide: !visible,
          pinned,
          width,
        }));
      return result;
    }, {} as Record<ET, ColDef[]>);
  }, [viewStates]);

  const handleFetchDrillDownData = useCallback(
    (payload: DrillDownListFetcherPayload<AnyEntity>) =>
      onFetchDrillDownData({ ...payload, entityType }),
    [entityType, onFetchDrillDownData]
  );

  const handleExportDrillDownData = useCallback(() => {
    analytics.clicked(["Export"]);
    onExportDrillDownData?.(entityType);
  }, [entityType, onExportDrillDownData, analytics]);

  const tabs = useMemo(
    () =>
      allEntityTypes.map((entityType) => ({
        disabled: !entityTypes.includes(entityType),
        key: entityType,
        label: getEntityTypeDisplayName(intl, entityType as EntityType, {
          lowercase: false,
          plural: true,
        }),
      })),
    [allEntityTypes, entityTypes, intl]
  );

  const handleEdit = useCallback(() => {
    analytics.clicked(["Edit"]);
    onHide();
    onEdit?.();
  }, [onHide, onEdit, analytics]);

  return (
    <AgGridListViewProvider<AnyEntity>
      columnDefs={columnDefs[entityType]}
      entityType={entityType as EntityType}
      fieldModel={getFieldModelByEntityType(entityType)}
      key={entityType}
      listFetcher={handleFetchDrillDownData}
      listViewState={viewStates[entityType] ?? EMPTY_LIST_VIEW_STATE}
    >
      <Modal
        actions={<Actions onEdit={handleEdit} showSelectColumnsButton={false} />}
        className={styles.modal}
        closable
        footer={null}
        onCancel={onHide}
        open
        scrollable
        subtitle={subtitle}
        title={title}
      >
        <AgGridListViewSection>
          <Tabs
            activeKey={entityType}
            className={styles.tabs}
            items={tabs}
            onChange={handleChangeTab}
            tabBarExtraContent={{
              right: (
                <div className={styles.tabActions}>
                  {canDownload && onExportDrillDownData ? (
                    <ExportButton onExport={handleExportDrillDownData} />
                  ) : null}
                  <SelectColumnsButton fieldFilter={skipNonListViewFields} />
                </div>
              ),
            }}
          />
          <Grid
            context={context}
            frameworkComponents={gridFrameworkComponents}
            loading={loading}
            onActivityTypeView={onActivityTypeView}
            onRecordView={onRecordView}
            totalFilteredRecords={totalFilteredRecords[entityType] ?? 0}
          />
          <AgGridListViewPagination
            className={styles.pagination}
            showSizeChanger
            totalRecords={totalFilteredRecords[entityType] ?? 0}
          />
        </AgGridListViewSection>
      </Modal>
    </AgGridListViewProvider>
  );
};

const mapStateToProps = (state: RootState) => ({
  canDownload: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
});

export default connect(mapStateToProps)(TabbedDrillDownModal) as <
  ET extends CanonicalEntityTypeWithFieldModel,
  VS extends DrillDownViewState = DrillDownViewState
>(
  props: TabbedDrillDownModalProps<ET, VS>
) => ReactElement;
